import { useState } from 'react';
import { api, type ChangesSinceLive } from '../../api';
import { useResource } from '../../hooks';

/**
 * ChangesSinceLiveNote says what a deployment changes compared with the
 * release live in the environment (UX-07): the commits, newest first, or
 * why that is not known. Read from the Git provider through the same
 * credential a checkout uses; never guessed.
 */
export function ChangesSinceLiveNote({
  projectId,
  applicationId,
  environmentId,
  commit,
}: {
  projectId: string;
  applicationId: string;
  environmentId: string;
  commit: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const changes = useResource<ChangesSinceLive | null>(
    () => (commit && commit.length >= 7 ? api.getChangesSinceLive(projectId, applicationId, environmentId, commit) : Promise.resolve(null)),
    [projectId, applicationId, environmentId, commit],
    commit,
  );
  if (!commit || commit.length < 7) return null;
  if (changes.loading && !changes.data) return <p className="release-picker__changes muted">Comparing with live…</p>;
  if (changes.error) return <p className="release-picker__changes muted">Changes since live · not read</p>;
  const data = changes.data;
  if (!data) return null;
  if (data.state === 'no_live_release') {
    return <p className="release-picker__changes" data-testid="changes-since-live">First release · nothing of this component is live yet</p>;
  }
  if (data.state === 'same_commit') {
    return (
      <p className="release-picker__changes" data-testid="changes-since-live">
        Already live{data.live_release_tag ? ` (${data.live_release_tag})` : ''} · no code changes
      </p>
    );
  }
  if (data.state === 'not_available' || !data.comparison) {
    return (
      <p className="release-picker__changes muted" data-testid="changes-since-live">
        Changes since live · not known: {data.reason?.replace(/\.$/, '') ?? 'the comparison did not answer'}
      </p>
    );
  }
  const { comparison } = data;
  if (comparison.status === 'behind') {
    return (
      <p className="release-picker__changes release-picker__changes--warn" data-testid="changes-since-live">
        Older than the live release <code>{data.live_commit?.slice(0, 7)}</code> · goes back {comparison.behind_by || 'some'} commit
        {comparison.behind_by === 1 ? '' : 's'}
      </p>
    );
  }
  const shown = expanded ? comparison.commits : comparison.commits.slice(0, 3);
  return (
    <div className="release-picker__changes" data-testid="changes-since-live">
      <p>
        <strong>
          {comparison.ahead_by} {comparison.ahead_by === 1 ? 'commit' : 'commits'} since the live release
        </strong>{' '}
        <code>{data.live_commit?.slice(0, 7)}</code>
        {comparison.status === 'diverged' ? <span className="muted"> (the branch also lost {comparison.behind_by} of the live commits)</span> : null}
      </p>
      <ul className="release-picker__commits">
        {shown.map((c) => (
          <li key={c.sha}>
            <code>{c.sha.slice(0, 7)}</code> {c.message}
            {c.author ? <span className="muted"> — {c.author}</span> : null}
          </li>
        ))}
      </ul>
      {comparison.commits.length > 3 ? (
        <button type="button" className="link-button" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show fewer' : `Show all ${comparison.commits.length}${comparison.ahead_by > comparison.commits.length ? ` of ${comparison.ahead_by}` : ''}`}
        </button>
      ) : null}
    </div>
  );
}
